"use strict";
/**
 * Multi-Product Package Types for CafeMaster Pro Platform
 *
 * This file contains all package and product-related types used across services.
 * DO NOT MOVE THIS FILE - it is referenced by multiple microservices.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.POS_BUTTON_REGISTRY = exports.DEFAULT_PACKAGES = exports.PRODUCTS = exports.PRODUCT_CONFIGS = exports.FEATURE_PACKAGE_MAP = exports.AI_FAMILY_SEED = exports.AI_TIER_SEED_ENTITLEMENTS = exports.AI_ACTION_FEATURES = exports.RUBY_TOKENS_METER = exports.AI_TIER_IDS = exports.AI_TIER_ENTERPRISE_ID = exports.AI_TIER_PRO_ID = exports.AI_TIER_DEFAULT_ID = exports.AI_FAMILY_ID = void 0;
exports.findMeterRule = findMeterRule;
exports.getPackageFeatures = getPackageFeatures;
exports.detectProduct = detectProduct;
exports.getProductConfig = getProductConfig;
exports.isFeatureAvailableForProduct = isFeatureAvailableForProduct;
exports.getButtonsForFeatures = getButtonsForFeatures;
exports.getButtonIdsForFeatures = getButtonIdsForFeatures;
/**
 * The meter rule for `resource`, or undefined. Callers MUST treat undefined
 * (and `included: null`) as unlimited — never as zero.
 */
function findMeterRule(entitlements, resource) {
    if (!Array.isArray(entitlements))
        return undefined;
    return entitlements.find((r) => r?.kind === 'meter' && r.resource === resource);
}
// ---------- Ruby AI family (spec 2026-09-27 D13) ----------
exports.AI_FAMILY_ID = 'fam_ruby_ai';
exports.AI_TIER_DEFAULT_ID = 'pkg_ai_default';
exports.AI_TIER_PRO_ID = 'pkg_ai_pro';
/** Enterprise is the EXISTING pkg_pos_agents — id, features and price unchanged. */
exports.AI_TIER_ENTERPRISE_ID = 'pkg_pos_agents';
/** Lowest first — the family's `tierIds`. */
exports.AI_TIER_IDS = [exports.AI_TIER_DEFAULT_ID, exports.AI_TIER_PRO_ID, exports.AI_TIER_ENTERPRISE_ID];
/** Meter resource for the monthly Ruby Token allowance. `included` is in RUBY TOKENS. */
exports.RUBY_TOKENS_METER = 'ruby_tokens';
/** Feature ids added for Ruby AI Actions & Vibes (all additive). */
exports.AI_ACTION_FEATURES = {
    /** Existing — text.product_description + default/Summer vibes. */
    description: 'ai-description',
    vibesSeasonal: 'ai-vibes-seasonal',
    vibesPremium: 'ai-vibes-premium',
    imageActions: 'ai-image-actions',
    legalActions: 'ai-legal-actions',
    videoActions: 'ai-video-actions',
    voiceActions: 'ai-voice-actions',
    /** 2026-10-08 — text.job_description + image.job_ad (Careers). */
    careersActions: 'ai-careers-actions',
    /** 2026-10-11 — Ruby Estate: data.property_lookup/valuation, data.rent_estimate, text.property_analysis. */
    estateActions: 'ai-estate-actions',
    /** 2026-10-17 — Document Management: documents.read_invoice (Ruby reads the invoice). Also needs documents.invoices. */
    documentsActions: 'ai-documents-actions',
};
/**
 * Seed entitlements per AI tier. `included` is Ruby Tokens:
 * Default 500 credits = 500K, Pro 2,000 credits = 2M, Enterprise 10,000
 * credits = 10M. Overage draws the purchased wallet. Owner-editable after
 * seeding; seeds never overwrite a live record.
 */
exports.AI_TIER_SEED_ENTITLEMENTS = {
    [exports.AI_TIER_DEFAULT_ID]: [
        { kind: 'meter', resource: exports.RUBY_TOKENS_METER, included: 500000, overage: { action: 'wallet' } },
    ],
    [exports.AI_TIER_PRO_ID]: [
        { kind: 'meter', resource: exports.RUBY_TOKENS_METER, included: 2000000, overage: { action: 'wallet' } },
    ],
    [exports.AI_TIER_ENTERPRISE_ID]: [
        { kind: 'meter', resource: exports.RUBY_TOKENS_METER, included: 10000000, overage: { action: 'wallet' } },
    ],
};
/** Seed for the Ruby AI family record (timestamps set by the writer). */
exports.AI_FAMILY_SEED = {
    id: exports.AI_FAMILY_ID,
    name: 'Ruby AI',
    description: 'Ruby AI Actions, vibes and a monthly Ruby Token allowance — Default, Pro, Enterprise',
    productType: 'pos',
    tierIds: [...exports.AI_TIER_IDS],
    addonIds: [],
    defaultTierId: exports.AI_TIER_DEFAULT_ID,
    noticeDays: 30,
    enrollment: 'manual',
    status: 'active',
};
// ========================
// Feature to Package Mapping
// ========================
/**
 * Maps feature keys to their required package ID.
 * Used by packageAuth middleware to check access.
 *
 * CRITICAL: When adding new features, always add mapping here.
 */
exports.FEATURE_PACKAGE_MAP = {
    // ========================
    // Shared Packages (available to all products)
    // ========================
    // Creator Studio Package (pkg_creator)
    'creator-studio': 'pkg_creator',
    'video-generation': 'pkg_creator',
    'composition-builder': 'pkg_creator',
    'menu-boards': 'pkg_creator',
    'public-shares': 'pkg_creator',
    'video-library': 'pkg_creator',
    'mini-menu-studio': 'pkg_creator',
    // Team Management Package (pkg_team)
    'user-management': 'pkg_team',
    'scheduling': 'pkg_team',
    'routines': 'pkg_team',
    'training': 'pkg_team',
    'employee-documents': 'pkg_team',
    'time-off': 'pkg_team',
    'availability': 'pkg_team',
    // Inventory Package (pkg_inventory)
    'inventory': 'pkg_inventory',
    'supplies': 'pkg_inventory',
    'stock-reports': 'pkg_inventory',
    'order-lists': 'pkg_inventory',
    // ========================
    // CafeMaster Specific Packages
    // ========================
    // Menu Package (pkg_menu) - CafeMaster only
    'recipes': 'pkg_menu',
    'menu-hub': 'pkg_menu',
    'categories': 'pkg_menu',
    'menu-groups': 'pkg_menu',
    'units': 'pkg_menu',
    // ========================
    // AgenticPOS Specific Packages
    // ========================
    // POS Catalog Package (pkg_pos_catalog)
    'pos.catalog.basic': 'pkg_pos_catalog',
    'pos.catalog.bulk-ops': 'pkg_pos_catalog',
    'pos.catalog.attachments': 'pkg_pos_catalog',
    'pos.catalog.age-restrict': 'pkg_pos_catalog',
    // POS Terminal Package (pkg_pos_terminal)
    'pos.terminal.basic': 'pkg_pos_terminal',
    'pos.terminal.split-payment': 'pkg_pos_terminal',
    'pos.terminal.hold-cart': 'pkg_pos_terminal',
    'pos.terminal.cash-mgmt': 'pkg_pos_terminal',
    'pos.terminal.refunds': 'pkg_pos_terminal',
    // POS Stock Package (pkg_pos_stock)
    'pos.stock.basic': 'pkg_pos_stock',
    'pos.stock.transfers': 'pkg_pos_stock',
    'pos.stock.counts': 'pkg_pos_stock',
    'pos.stock.po': 'pkg_pos_stock',
    'pos.stock.suppliers': 'pkg_pos_stock',
    'pos.stock.batch-serial': 'pkg_pos_stock',
    'pos.stock.valuation': 'pkg_pos_stock',
    // POS Customers Package (pkg_pos_customers)
    'pos.customers.basic': 'pkg_pos_customers',
    'pos.customers.loyalty': 'pkg_pos_customers',
    'pos.customers.rewards': 'pkg_pos_customers',
    'pos.customers.coupons': 'pkg_pos_customers',
    'pos.customers.tiers': 'pkg_pos_customers',
    'pos.customers.portal': 'pkg_pos_customers',
    'pos.customers.import': 'pkg_pos_customers',
    // POS Promotions (under Marketing)
    'pos.promos.basic': 'pkg_pos_marketing',
    'pos.promos.advanced': 'pkg_pos_marketing',
    'pos.promos.coupons': 'pkg_pos_marketing',
    'pos.promos.scheduling': 'pkg_pos_marketing',
    // POS Orders Package (pkg_pos_orders)
    'pos.orders.basic': 'pkg_pos_orders',
    'pos.orders.delivery': 'pkg_pos_orders',
    'pos.orders.drivers': 'pkg_pos_orders',
    'pos.orders.phone': 'pkg_pos_orders',
    'pos.orders.routing': 'pkg_pos_orders',
    'pos.orders.fulfillment': 'pkg_pos_orders',
    // POS Reports Package (pkg_pos_reports)
    'pos.reports.basic': 'pkg_pos_reports',
    'pos.reports.advanced': 'pkg_pos_reports',
    'pos.reports.exports': 'pkg_pos_reports',
    'pos.reports.scheduled': 'pkg_pos_reports',
    'pos.reports.daily_paperwork': 'pkg_pos_reports',
    // POS E-commerce Package (pkg_pos_ecommerce)
    'pos.ecommerce.storefront': 'pkg_pos_ecommerce',
    'pos.ecommerce.custom-domain': 'pkg_pos_ecommerce',
    'pos.ecommerce.marketplace': 'pkg_pos_ecommerce',
    // Unified storefront — Phase B (retail own-site online sales)
    'pos.ecommerce.online-sales': 'pkg_pos_retail_online',
    // Marketplace governance — Phase H (owner curates, seller lists)
    'pos.marketplace.owner-curation': 'pkg_pos_marketplace_owner',
    'pos.marketplace.nmi-commission': 'pkg_pos_marketplace_owner',
    'pos.marketplace.seller-listing': 'pkg_pos_marketplace_seller',
    // POS Payments Package (pkg_pos_payments)
    'pos.payments.multi-gateway': 'pkg_pos_payments',
    'pos.payments.emv': 'pkg_pos_payments',
    'pos.payments.external-cc': 'pkg_pos_payments',
    'pos.payments.split': 'pkg_pos_payments',
    'pos.payments.nmi_cloud': 'pkg_pos_payments',
    'pos.payments.nmi_direct': 'pkg_pos_payments',
    'pos.payments.tips': 'pkg_pos_payments',
    'pos.payments.surcharge': 'pkg_pos_payments',
    // POS Compliance Package (pkg_pos_compliance)
    'pos.compliance.age-verify': 'pkg_pos_compliance',
    'pos.compliance.purchase-limits': 'pkg_pos_compliance',
    'pos.compliance.id-scan': 'pkg_pos_compliance',
    'pos.compliance.audit': 'pkg_pos_compliance',
    // Phase G — online compliance features (online age verify, ID upload, delivery ID check, cross-channel limits, state shipping)
    'pos.compliance.online-age-verify': 'pkg_pos_compliance',
    'pos.compliance.online-id-upload': 'pkg_pos_compliance',
    'pos.compliance.delivery-id-check': 'pkg_pos_compliance',
    'pos.compliance.cross-channel-limits': 'pkg_pos_compliance',
    'pos.compliance.state-shipping-restrict': 'pkg_pos_compliance',
    // POS Cannabis Package (pkg_pos_cannabis)
    'pos.cannabis.seed-to-sale': 'pkg_pos_cannabis',
    'pos.cannabis.packages': 'pkg_pos_cannabis',
    'pos.cannabis.lab-tests': 'pkg_pos_cannabis',
    'pos.cannabis.allotments': 'pkg_pos_cannabis',
    // POS AI Agents Package (pkg_pos_agents)
    'pos.agents.salesguide': 'pkg_pos_agents',
    'pos.agents.inventory': 'pkg_pos_agents',
    'pos.agents.compliance': 'pkg_pos_agents',
    'pos.agents.analytics': 'pkg_pos_agents',
    // POS Marketing Package (pkg_pos_marketing)
    'pos.marketing.google-business': 'pkg_pos_marketing',
    // 2026-10-13 Ruby Social Publishing — one feature per channel. Google photo publishing is
    // google-business-photos; google-business stays the Marketing (Nav) menu feature.
    'pos.marketing.google-business-photos': 'pkg_pos_marketing',
    'pos.marketing.facebook': 'pkg_pos_marketing',
    'pos.marketing.instagram': 'pkg_pos_marketing',
    'pos.marketing.tv-display': 'pkg_pos_marketing',
    'pos.marketing.ad-platform': 'pkg_pos_marketing',
    // POS Advertising Displays Package (pkg_pos_advertising) — new 2026-05-21
    'pos.advertising.displays': 'pkg_pos_advertising',
    'pos.advertising.playlists': 'pkg_pos_advertising',
    'pos.advertising.media-library': 'pkg_pos_advertising',
    // POS Lottery & Gaming Package (pkg_pos_lottery)
    'pos.lottery.games': 'pkg_pos_lottery',
    'pos.lottery.scratch-off': 'pkg_pos_lottery',
    'pos.lottery.draw-games': 'pkg_pos_lottery',
    'pos.lottery.settlements': 'pkg_pos_lottery',
    'pos.lottery.claims': 'pkg_pos_lottery',
    // POS Lottery & Gaming — till button, gaming vouchers, reports (2026-09-26)
    'pos.button.lottery': 'pkg_pos_lottery',
    'pos.lottery.gaming': 'pkg_pos_lottery',
    'pos.lottery.reports': 'pkg_pos_lottery',
    // Store Credit (2026-09-26) — core feature, all products
    'pos.store-credit': 'pkg_core',
    // POS B2B Wholesale Package (pkg_pos_b2b)
    'pos.b2b.wholesale': 'pkg_pos_b2b',
    'pos.b2b.net-terms': 'pkg_pos_b2b',
    'pos.b2b.invoicing': 'pkg_pos_b2b',
    'pos.b2b.multi-ein': 'pkg_pos_b2b',
    // Retail Menu Manager Package (pkg_pos_menu)
    'pos.menu.recipes': 'pkg_pos_menu',
    // Clothing & Accessories product type
    'apparel': 'pkg_apparel',
    'pos.menu.modifiers': 'pkg_pos_menu',
    'pos.menu.ingredients': 'pkg_pos_menu',
    'pos.menu.depletion': 'pkg_pos_menu',
    'pos-menu': 'pkg_pos_menu',
    'pos.menu.menus': 'pkg_pos_menu',
    'pos.menu.deals': 'pkg_pos_menu',
    'pos.menu.pizza': 'pkg_pos_menu',
    'pos.menu.training': 'pkg_pos_menu',
    'pos.kitchen.stock': 'pkg_pos_menu',
    'pos.kitchen.waste': 'pkg_pos_menu',
    'pos.kitchen.kds': 'pkg_pos_menu',
    // POS Product Addon Feature Keys (mapped to existing POS packages)
    'compliance-safety': 'pkg_pos_compliance',
    'marketing': 'pkg_pos_marketing',
    'product-reviews': 'pkg_pos_marketing',
    'pos-ecommerce': 'pkg_pos_ecommerce',
    'pos-b2b': 'pkg_pos_b2b',
    'promotions': 'pkg_pos_marketing',
    'deals': 'pkg_pos_marketing',
    'loyalty-vip': 'pkg_pos_customers',
    'smart-image-search': 'pkg_pos_agents',
    'ai-description': 'pkg_pos_agents',
    'ai-routine-builder': 'pkg_pos_agents',
    'ai-insights': 'pkg_pos_agents',
    // New AI agent features for future extensions
    'pos.agents.upsell': 'pkg_pos_agents',
    'pos.agents.cross-sell': 'pkg_pos_agents',
    'pos.agents.clerk-suggestions': 'pkg_pos_agents',
    'pos.agents.anomaly-alerts': 'pkg_pos_agents',
    'pos.agents.forecasting': 'pkg_pos_agents',
    'pos.agents.scheduled-reports': 'pkg_pos_agents',
    // Ruby AI Actions & Vibes (2026-09-27). Mapped to pkg_pos_agents (Enterprise,
    // the top tier, which holds everything) so no existing tenant loses access.
    // Lower tiers (pkg_ai_default / pkg_ai_pro) carry their subset in features[].
    'ai-vibes-seasonal': 'pkg_pos_agents',
    'ai-vibes-premium': 'pkg_pos_agents',
    'ai-image-actions': 'pkg_pos_agents',
    'ai-legal-actions': 'pkg_pos_agents',
    'ai-video-actions': 'pkg_pos_agents',
    'ai-voice-actions': 'pkg_pos_agents',
    'ai-careers-actions': 'pkg_pos_agents',
    'ai-estate-actions': 'pkg_pos_agents',
    'ai-documents-actions': 'pkg_pos_agents',
    // ========================
    // POS Action Buttons (feature gates for terminal action buttons)
    // ========================
    // Core terminal buttons (pkg_pos_terminal)
    'pos.button.void': 'pkg_pos_terminal',
    'pos.button.refund': 'pkg_pos_terminal',
    'pos.button.exchange': 'pkg_pos_terminal',
    'pos.button.discount': 'pkg_pos_terminal',
    'pos.button.hold': 'pkg_pos_terminal',
    'pos.button.open-order': 'pkg_pos_terminal',
    'pos.button.search': 'pkg_pos_terminal',
    'pos.button.end-shift': 'pkg_pos_terminal',
    // Customer & loyalty buttons (pkg_pos_customers)
    'pos.button.customer': 'pkg_pos_customers',
    'pos.button.rewards': 'pkg_pos_customers',
    'pos.button.coupon': 'pkg_pos_customers',
    'pos.button.cash-tip': 'pkg_pos_terminal',
    // Sales & deals buttons (pkg_pos_marketing)
    'pos.button.deals': 'pkg_pos_marketing',
    'pos.commissions': 'pkg_pos_marketing',
    // Inventory buttons (pkg_pos_stock)
    'pos.button.damage': 'pkg_pos_stock',
    // Register management buttons (pkg_pos_terminal)
    'pos.button.open-drawer': 'pkg_pos_terminal',
    'pos.button.safe-drop': 'pkg_pos_terminal',
    'pos.button.calculator': 'pkg_pos_terminal',
    // Reports buttons (pkg_pos_reports)
    'pos.button.reports': 'pkg_pos_reports',
    'pos.button.transactions': 'pkg_pos_reports',
    // Employee management buttons (pkg_pos_terminal)
    'pos.button.clock': 'pkg_pos_terminal',
    'pos.button.shift-info': 'pkg_pos_terminal',
    // Store Supply button (pkg_mn7wqu8z_u8gnw6)
    'pos.button.store-supply': 'pkg_mn7wqu8z_u8gnw6',
    // ========================
    // Oil Blending (Module 29) — buttons & feature gates
    // ========================
    // Package: pkg_pos_oil_blending. Module covers custom on-the-spot
    // fragrance/carrier oil blending — catalog, builder, customer portal,
    // refills, signature library. Multi-tenant: every feature key flows
    // through hasFeature() against the tenant's enabled packages.
    'pos.button.build-blend': 'pkg_pos_oil_blending',
    'pos.button.pending-blends': 'pkg_pos_oil_blending',
    'pos.button.customer-display': 'pkg_pos_oil_blending',
    'oil-blending.catalog': 'pkg_pos_oil_blending',
    'oil-blending.blend-builder': 'pkg_pos_oil_blending',
    'oil-blending.signature-library': 'pkg_pos_oil_blending',
    'oil-blending.customer-history': 'pkg_pos_oil_blending',
    'oil-blending.byob-refill': 'pkg_pos_oil_blending',
    'oil-blending.tweak-remix': 'pkg_pos_oil_blending',
    'oil-blending.auto-classify': 'pkg_pos_oil_blending',
    'oil-blending.suggestion-engine': 'pkg_pos_oil_blending',
    'oil-blending.reorder-alerts': 'pkg_pos_oil_blending',
    'oil-blending.reports': 'pkg_pos_oil_blending',
    'oil-blending.customer-portal': 'pkg_pos_oil_blending',
    // ========================
    // Services package (2026-09-26)
    // ========================
    'pos.services.items': 'pkg_services',
    'pos.services.tickets': 'pkg_services',
    'pos.services.appointments': 'pkg_services',
    'pos.services.checkin': 'pkg_services',
    'pos.services.online-booking': 'pkg_services',
    'pos.services.reminders': 'pkg_services',
    'pos.services.rentals': 'pkg_services',
    'pos.button.services': 'pkg_services',
    // Core features (always available)
    'pos.locations': 'pkg_core',
    // Gas & Fuel — prepaid fuel at the till. In core so any tenant can switch it on per location.
    'pos.gas': 'pkg_core',
    'pos.button.gas': 'pkg_core',
    // ========================
    // RealEstate Specific Packages (future)
    // ========================
    // Property Package (pkg_property)
    'properties': 'pkg_property',
    'listings': 'pkg_property',
    // Property Management Package (pkg_property_mgmt)
    'leases': 'pkg_property_mgmt',
    'tenants-mgmt': 'pkg_property_mgmt', // renamed to avoid conflict with platform tenants
    'maintenance': 'pkg_property_mgmt',
    // ========================
    // Planning Module (Management Essentials) - Shared
    // ========================
    // Management Package (pkg_management)
    'management.dashboard': 'pkg_management',
    'management.goals': 'pkg_management',
    'management.goals.view': 'pkg_management',
    'management.board': 'pkg_management',
    'management.workplans': 'pkg_management',
    'management.workplans.view': 'pkg_management',
    'management.meetings': 'pkg_management',
    'management.meetings.view': 'pkg_management',
    'management.feed': 'pkg_management',
    'management.team': 'pkg_management',
    'management.roles': 'pkg_management',
    'management.mywork': 'pkg_management',
    // ========================
    // Document Management (2026-10-17) — family fam_documents, tiers
    // pkg_documents_basic|pro|advanced all carry this feature. The map holds ONE
    // package per feature, so it names the entry tier.
    // ========================
    'documents.invoices': 'pkg_documents_basic',
};
/**
 * Reverse mapping: Package ID to features
 * Useful for displaying what's included in a package
 */
function getPackageFeatures(packageId) {
    return Object.entries(exports.FEATURE_PACKAGE_MAP)
        .filter(([_, pkgId]) => pkgId === packageId)
        .map(([feature, _]) => feature);
}
exports.PRODUCT_CONFIGS = {
    cafemaster: {
        id: 'cafemaster',
        name: 'CafeMaster Pro',
        domain: 'cafemenumaster.com',
        devDomain: 'dev.coffeemasterpro.com',
        appDomain: 'app.cafemenumaster.com',
        devAppDomain: 'app.dev.coffeemasterpro.com',
        theme: {
            primaryColor: '#78350F',
            secondaryColor: '#92400E',
        },
        defaultRoute: '/menu-hub',
        features: [
            'menu-hub', 'recipes', 'categories', 'menu-groups', 'units',
            'inventory', 'supplies', 'stock-reports', 'order-lists',
            'scheduling', 'routines', 'training', 'user-management',
            'creator-studio', 'video-generation', 'composition-builder',
        ],
    },
    pos: {
        id: 'pos',
        name: 'AgenticPOS',
        domain: 'agenticpos.app',
        devDomain: 'dev.agenticpos.app',
        appDomain: 'app.agenticpos.app',
        devAppDomain: 'app.dev.agenticpos.app',
        theme: {
            primaryColor: '#059669',
            secondaryColor: '#10B981',
        },
        defaultRoute: '/pos/dashboard',
        features: [
            // Catalog
            'pos.catalog.basic', 'pos.catalog.bulk-ops', 'pos.catalog.attachments', 'pos.catalog.age-restrict',
            // Terminal
            'pos.terminal.basic', 'pos.terminal.split-payment', 'pos.terminal.hold-cart', 'pos.terminal.cash-mgmt', 'pos.terminal.refunds',
            // Stock
            'pos.stock.basic', 'pos.stock.transfers', 'pos.stock.counts', 'pos.stock.po', 'pos.stock.suppliers', 'pos.stock.batch-serial', 'pos.stock.valuation',
            // Customers
            'pos.customers.basic', 'pos.customers.loyalty', 'pos.customers.rewards', 'pos.customers.coupons', 'pos.customers.tiers', 'pos.customers.portal', 'pos.customers.import',
            // Promotions
            'pos.promos.basic', 'pos.promos.advanced', 'pos.promos.coupons', 'pos.promos.scheduling',
            // Orders
            'pos.orders.basic', 'pos.orders.delivery', 'pos.orders.routing', 'pos.orders.fulfillment', 'pos.orders.drivers', 'pos.orders.phone',
            // Reports
            'pos.reports.basic', 'pos.reports.advanced', 'pos.reports.exports', 'pos.reports.scheduled',
            // E-commerce
            'pos.ecommerce.storefront', 'pos.ecommerce.custom-domain', 'pos.ecommerce.marketplace',
            // Marketplace (Phase I recast — owner curates, seller lists, NMI commission)
            'pos.marketplace.owner-curation', 'pos.marketplace.nmi-commission', 'pos.marketplace.seller-listing',
            // Payments
            'pos.payments.multi-gateway', 'pos.payments.emv', 'pos.payments.external-cc', 'pos.payments.split',
            'pos.payments.nmi_cloud', 'pos.payments.nmi_direct', 'pos.payments.tips', 'pos.payments.surcharge',
            // Compliance
            'pos.compliance.age-verify', 'pos.compliance.purchase-limits', 'pos.compliance.id-scan', 'pos.compliance.audit',
            'pos.compliance.online-age-verify', 'pos.compliance.online-id-upload', 'pos.compliance.delivery-id-check', 'pos.compliance.cross-channel-limits', 'pos.compliance.state-shipping-restrict',
            // Cannabis
            'pos.cannabis.seed-to-sale', 'pos.cannabis.packages', 'pos.cannabis.lab-tests', 'pos.cannabis.allotments',
            // AI Agents
            'pos.agents.salesguide', 'pos.agents.inventory', 'pos.agents.compliance', 'pos.agents.analytics',
            // Marketing
            'pos.marketing.google-business', 'pos.marketing.google-business-photos', 'pos.marketing.facebook', 'pos.marketing.instagram', 'pos.marketing.tv-display', 'pos.marketing.ad-platform',
            // B2B Wholesale
            'pos.b2b.wholesale', 'pos.b2b.net-terms', 'pos.b2b.invoicing', 'pos.b2b.multi-ein',
            // Menu & Recipes
            'pos.menu.recipes', 'pos.menu.modifiers', 'pos.menu.ingredients', 'pos.menu.depletion',
            'pos.menu.menus', 'pos.menu.deals', 'pos.menu.pizza', 'pos.menu.training', 'pos.kitchen.stock', 'pos.kitchen.waste', 'pos.kitchen.kds',
            // Lottery & Gaming
            'pos.lottery.games', 'pos.lottery.scratch-off', 'pos.lottery.draw-games', 'pos.lottery.settlements', 'pos.lottery.claims',
            'pos.button.lottery', 'pos.lottery.gaming', 'pos.lottery.reports',
            // Gas & Fuel
            'pos.gas',
            // Services
            'pos.services.items', 'pos.services.tickets', 'pos.services.appointments', 'pos.services.checkin',
            'pos.services.online-booking', 'pos.services.reminders', 'pos.services.rentals',
            // Shared features
            'scheduling', 'user-management', 'creator-studio',
            // Core features
            'pos.locations',
        ],
    },
    realestate: {
        id: 'realestate',
        name: 'Jesmin Real Estate',
        domain: 'jesminrealestate.com',
        devDomain: 'realestate.dev.coffeemasterpro.com',
        appDomain: 'app.jesminrealestate.com',
        devAppDomain: 'app.realestate.dev.coffeemasterpro.com',
        theme: {
            primaryColor: '#1E40AF',
            secondaryColor: '#3B82F6',
        },
        defaultRoute: '/properties',
        features: [
            'properties', 'listings',
            'leases', 'tenants-mgmt', 'maintenance',
            'scheduling', 'user-management',
            'creator-studio',
        ],
    },
};
/**
 * Additive alias (Phase I): callers/tests reference the product registry as
 * `PRODUCTS`. Kept in sync with `PRODUCT_CONFIGS`; does not alter it.
 */
exports.PRODUCTS = exports.PRODUCT_CONFIGS;
// ========================
// Default Package Definitions
// ========================
/**
 * Default packages for seeding
 * These are the initial package definitions
 */
exports.DEFAULT_PACKAGES = [
    // Core Package (included with all subscriptions)
    {
        id: 'pkg_core',
        name: 'Core Platform',
        description: 'Essential platform features including authentication, settings, and basic analytics',
        productType: null,
        category: 'shared',
        status: 'active',
        features: ['settings', 'profile', 'notifications', 'basic-analytics', 'pos.locations', 'pos.gas', 'pos.button.gas', 'pos.store-credit'],
        services: ['auth-service', 'tenants-service'],
        basePrice: 0,
        displayOrder: 0,
        isCore: true,
        dependencies: [],
    },
    // Creator Studio Package
    {
        id: 'pkg_creator',
        name: 'Creator Studio',
        description: 'AI-powered content creation including video generation, menu boards, and social media assets',
        productType: null,
        category: 'shared',
        status: 'active',
        features: ['creator-studio', 'video-generation', 'composition-builder', 'menu-boards', 'public-shares', 'video-library', 'mini-menu-studio'],
        services: ['creator-service', 'media-service', 'uploads-service'],
        basePrice: 2999, // $29.99/month
        displayOrder: 1,
        isCore: false,
        dependencies: ['pkg_core'],
    },
    // Team Management Package
    {
        id: 'pkg_team',
        name: 'Team Management',
        description: 'Employee management, scheduling, routines, and training programs',
        productType: null,
        category: 'shared',
        status: 'active',
        features: ['user-management', 'scheduling', 'routines', 'training', 'employee-documents', 'time-off', 'availability'],
        services: ['users-service', 'scheduling-service', 'routines-service', 'training-service'],
        basePrice: 1999, // $19.99/month
        displayOrder: 2,
        isCore: false,
        dependencies: ['pkg_core'],
    },
    // Inventory Package
    {
        id: 'pkg_inventory',
        name: 'Inventory Management',
        description: 'Stock tracking, supplies management, and order list generation',
        productType: null,
        category: 'shared',
        status: 'active',
        features: ['inventory', 'supplies', 'stock-reports', 'order-lists'],
        services: ['inventory-service'],
        basePrice: 999, // $9.99/month
        displayOrder: 3,
        isCore: false,
        dependencies: ['pkg_core'],
    },
    // CafeMaster Menu Package
    {
        id: 'pkg_menu',
        name: 'Menu Management',
        description: 'Recipe management, menu categories, and Menu Hub for CafeMaster',
        productType: 'cafemaster',
        category: 'product-specific',
        status: 'active',
        features: ['recipes', 'menu-hub', 'categories', 'menu-groups', 'units'],
        services: ['recipes-service'],
        basePrice: 0, // Included with CafeMaster subscription
        displayOrder: 10,
        isCore: false,
        dependencies: ['pkg_core'],
    },
    // AgenticPOS Packages
    {
        id: 'pkg_pos_catalog',
        name: 'POS Catalog',
        description: 'Product catalog, variants, barcodes, bulk operations, age restrictions',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.catalog.basic', 'pos.catalog.bulk-ops', 'pos.catalog.attachments', 'pos.catalog.age-restrict'],
        services: ['products-service'],
        basePrice: 0,
        displayOrder: 20,
        isCore: false,
        dependencies: ['pkg_core'],
    },
    {
        id: 'pkg_pos_terminal',
        name: 'POS Terminal',
        description: 'Cart, checkout, split payments, hold/recall, cash management, refunds',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: [
            'pos.terminal.basic', 'pos.terminal.split-payment', 'pos.terminal.hold-cart', 'pos.terminal.cash-mgmt', 'pos.terminal.refunds',
            // POS action buttons gated to this package
            'pos.button.void', 'pos.button.refund', 'pos.button.exchange', 'pos.button.discount',
            'pos.button.hold', 'pos.button.open-order', 'pos.button.search', 'pos.button.end-shift',
            'pos.button.open-drawer', 'pos.button.safe-drop', 'pos.button.calculator', 'pos.button.clock', 'pos.button.shift-info',
            'pos.button.cash-tip',
        ],
        services: ['pos-service'],
        basePrice: 0,
        displayOrder: 21,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    {
        id: 'pkg_pos_stock',
        name: 'POS Stock Management',
        description: 'GRN, transfers, stock counts, purchase orders, suppliers, batch/serial tracking, valuation',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.stock.basic', 'pos.stock.transfers', 'pos.stock.counts', 'pos.stock.po', 'pos.stock.suppliers', 'pos.stock.batch-serial', 'pos.stock.valuation', 'pos.button.damage'],
        services: ['stock-service'],
        basePrice: 0,
        displayOrder: 22,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    {
        id: 'pkg_pos_customers',
        name: 'POS Customers',
        description: 'Customer profiles, loyalty points, membership tiers, customer portal',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.customers.basic', 'pos.customers.loyalty', 'pos.customers.rewards', 'pos.customers.coupons', 'pos.customers.tiers', 'pos.customers.portal', 'pos.customers.import', 'loyalty-vip', 'pos.button.customer', 'pos.button.rewards', 'pos.button.coupon'],
        services: ['customers-service'],
        basePrice: 0,
        displayOrder: 23,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_terminal'],
    },
    {
        id: 'pkg_pos_orders',
        name: 'POS Orders',
        description: 'Online orders, delivery routing, fulfillment tracking',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.orders.basic', 'pos.orders.delivery', 'pos.orders.routing', 'pos.orders.fulfillment', 'pos.orders.drivers', 'pos.orders.phone'],
        services: ['orders-service'],
        basePrice: 0,
        displayOrder: 25,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_customers'],
    },
    {
        id: 'pkg_pos_reports',
        name: 'POS Reports',
        description: 'Dashboards, analytics, exports, scheduled reports',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.reports.basic', 'pos.reports.advanced', 'pos.reports.exports', 'pos.reports.scheduled', 'pos.reports.daily_paperwork', 'pos.button.reports', 'pos.button.transactions'],
        services: ['pos-reporting-service'],
        basePrice: 0,
        displayOrder: 26,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_terminal'],
    },
    {
        id: 'pkg_pos_retail_online',
        name: 'POS Online Sales',
        description: "A store's own retail website (.online channel): branded storefront, subdomain, customer accounts",
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.ecommerce.online-sales', 'pos.ecommerce.storefront', 'pos.ecommerce.custom-domain'],
        services: ['ecommerce-service'],
        basePrice: 0,
        displayOrder: 27,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    // Marketplace governance (Phase H) — two-level: owner runs a marketplace,
    // seller lists inside an owner's marketplace. productType MUST be 'pos' to
    // match the existing pos-online discriminant (pkg_pos_retail_online etc.).
    {
        id: 'pkg_pos_marketplace_owner',
        name: 'POS Marketplace Owner',
        description: 'Run your own marketplace: aggregate, approve, and curate other Ruby-Till sellers on your store-site.',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.marketplace.owner-curation', 'pos.marketplace.nmi-commission'],
        services: ['ecommerce-service', 'marketplace-owner-service'],
        basePrice: 0,
        displayOrder: 27,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_retail_online'],
    },
    {
        id: 'pkg_pos_marketplace_seller',
        name: 'POS Marketplace Seller',
        description: "Be listed inside an owner's marketplace (with that owner's permission).",
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.marketplace.seller-listing'],
        services: ['ecommerce-service'],
        basePrice: 0,
        displayOrder: 27,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    {
        id: 'pkg_pos_ecommerce',
        name: 'POS E-commerce',
        description: 'Storefront, custom domains, marketplace multi-tenant listing',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.ecommerce.storefront', 'pos.ecommerce.custom-domain', 'pos.ecommerce.marketplace', 'pos-ecommerce'],
        services: ['ecommerce-service'],
        basePrice: 0,
        displayOrder: 27,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_orders'],
    },
    {
        id: 'pkg_pos_payments',
        name: 'POS Payments',
        description: 'Multi-gateway (NMI/Stripe), EMV devices, external CC recording, split payments',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.payments.multi-gateway', 'pos.payments.emv', 'pos.payments.external-cc', 'pos.payments.split', 'pos.payments.nmi_cloud', 'pos.payments.nmi_direct', 'pos.payments.tips', 'pos.payments.surcharge'],
        services: ['payments-service'],
        basePrice: 0,
        displayOrder: 28,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_terminal'],
    },
    {
        id: 'pkg_pos_compliance',
        name: 'POS Compliance',
        description: 'Age verification, purchase limits, ID scanning, audit trail',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.compliance.age-verify', 'pos.compliance.purchase-limits', 'pos.compliance.id-scan', 'pos.compliance.audit', 'compliance-safety', 'pos.compliance.online-age-verify', 'pos.compliance.online-id-upload', 'pos.compliance.delivery-id-check', 'pos.compliance.cross-channel-limits', 'pos.compliance.state-shipping-restrict'],
        services: ['compliance-service'],
        basePrice: 0,
        displayOrder: 29,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_terminal'],
    },
    {
        id: 'pkg_pos_cannabis',
        name: 'POS Cannabis',
        description: 'Seed-to-sale (METRC/BioTrack), package tracking, lab tests, patient allotments',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.cannabis.seed-to-sale', 'pos.cannabis.packages', 'pos.cannabis.lab-tests', 'pos.cannabis.allotments'],
        services: ['cannabis-service'],
        basePrice: 0,
        displayOrder: 30,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_compliance'],
    },
    {
        id: 'pkg_pos_agents',
        name: 'POS AI Agents',
        description: 'SalesGuide, Inventory, Compliance, Analytics AI agents via Claude API',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: [
            'pos.agents.salesguide',
            'pos.agents.inventory',
            'pos.agents.compliance',
            'pos.agents.analytics',
            'pos.agents.upsell',
            'pos.agents.cross-sell',
            'pos.agents.clerk-suggestions',
            'pos.agents.anomaly-alerts',
            'pos.agents.forecasting',
            'pos.agents.scheduled-reports',
            'smart-image-search',
            'ai-description',
            'ai-insights',
            // Ruby AI Actions (2026-09-27) — Enterprise = everything; additions only.
            'ai-vibes-seasonal',
            'ai-vibes-premium',
            'ai-image-actions',
            'ai-legal-actions',
            'ai-video-actions',
            'ai-voice-actions',
            'ai-careers-actions',
            'ai-estate-actions',
            'ai-documents-actions',
        ],
        services: ['pos-agents-service'],
        basePrice: 0,
        displayOrder: 31,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_reports'],
    },
    // Ruby AI tiers (spec 2026-09-27 D13). Enterprise is pkg_pos_agents above.
    // Features are a strict subset of pkg_pos_agents (mockup board 6).
    {
        id: 'pkg_ai_default',
        name: 'Ruby AI — Default',
        description: 'Ruby AI product descriptions with the Professional and Summer vibes, plus a monthly Ruby Token allowance',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['ai-description', 'ai-careers-actions', 'ai-estate-actions', 'ai-documents-actions'],
        services: ['pos-agents-service'],
        basePrice: 0,
        displayOrder: 39,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
        familyId: 'fam_ruby_ai',
        tierRank: 1,
        role: 'tier',
        entitlements: [
            { kind: 'meter', resource: 'ruby_tokens', included: 500000, overage: { action: 'wallet' } },
        ],
    },
    {
        id: 'pkg_ai_pro',
        name: 'Ruby AI — Pro',
        description: 'Everything in Default plus image actions and seasonal vibes (Christmas), with a larger monthly Ruby Token allowance',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['ai-description', 'ai-image-actions', 'ai-vibes-seasonal', 'ai-careers-actions', 'ai-estate-actions', 'ai-documents-actions'],
        services: ['pos-agents-service'],
        basePrice: 0,
        displayOrder: 40,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
        familyId: 'fam_ruby_ai',
        tierRank: 2,
        role: 'tier',
        entitlements: [
            { kind: 'meter', resource: 'ruby_tokens', included: 2000000, overage: { action: 'wallet' } },
        ],
    },
    {
        id: 'pkg_pos_marketing',
        name: 'POS Marketing & Promotions',
        description: 'Promotions, discounts, BOGO, coupons, Google Business sync, in-store TV display, advertising',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.marketing.google-business', 'pos.marketing.google-business-photos', 'pos.marketing.facebook', 'pos.marketing.instagram', 'pos.marketing.tv-display', 'pos.marketing.ad-platform', 'marketing', 'product-reviews', 'pos.promos.basic', 'pos.promos.advanced', 'pos.promos.coupons', 'pos.promos.scheduling', 'promotions', 'deals', 'pos.button.deals', 'pos.commissions'],
        services: ['advertising-service', 'promotions-service'],
        basePrice: 0,
        displayOrder: 32,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    {
        id: 'pkg_pos_advertising',
        name: 'POS Advertising Displays',
        description: 'In-store TV display system: playlist editor, slide types (product/video/image/text), media library, and TV viewer at advertising.{domain}',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.advertising.displays', 'pos.advertising.playlists', 'pos.advertising.media-library'],
        services: ['pos-advertising-service'],
        basePrice: 0,
        displayOrder: 32,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    {
        id: 'pkg_pos_b2b',
        name: 'POS B2B Wholesale',
        description: 'Wholesale pricing tiers, net terms, invoicing, multi-EIN inter-entity transfers',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['pos.b2b.wholesale', 'pos.b2b.net-terms', 'pos.b2b.invoicing', 'pos.b2b.multi-ein', 'pos-b2b'],
        services: ['stock-service', 'orders-service', 'payments-service'],
        basePrice: 0,
        displayOrder: 33,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_stock'],
    },
    {
        id: 'pkg_pos_menu',
        name: 'Retail Menu Manager',
        description: 'Import recipes as POS-sellable products with modifiers, per-location pricing, and automatic ingredient depletion',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.menu.recipes', 'pos.menu.modifiers', 'pos.menu.ingredients', 'pos.menu.depletion', 'pos-menu',
            'pos.menu.menus', 'pos.menu.deals', 'pos.menu.pizza', 'pos.menu.training', 'pos.kitchen.stock', 'pos.kitchen.waste', 'pos.kitchen.kds'],
        services: ['kitchen-service', 'products-service-ext', 'stock-service'],
        basePrice: 0,
        displayOrder: 35,
        isCore: false,
        // pkg_menu is CafeMaster-only (recipes-service); RubyTill recipes live in kitchen-service.
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_stock'],
    },
    {
        id: 'pkg_apparel',
        name: 'Clothing & Accessories',
        description: 'Color × size products with per-color photos and pricing, grid receiving and transfers',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['apparel'],
        services: ['products-service', 'stock-service'],
        basePrice: 3999, // $39.99/month — per tenant, like every package (only devices bill per location)
        displayOrder: 36,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_stock'],
    },
    {
        id: 'pkg_pos_lottery',
        name: 'POS Lottery & Gaming',
        description: 'Scratch-off ticket books, draw games (Pick 3, Powerball), prize claims, commission settlements',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: ['pos.lottery.games', 'pos.lottery.scratch-off', 'pos.lottery.draw-games', 'pos.lottery.settlements', 'pos.lottery.claims', 'pos.button.lottery', 'pos.lottery.gaming', 'pos.lottery.reports'],
        services: ['lottery-service'],
        basePrice: 0,
        displayOrder: 36,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog'],
    },
    // Oil Blending (Module 29) — custom on-the-spot fragrance/carrier oil blending.
    // Multi-tenant: every feature key flows through hasFeature(); per-register
    // button placement uses the existing PosButtonLayoutTab UI which reads from
    // POS_BUTTON_REGISTRY filtered by enabled features.
    {
        id: 'pkg_pos_oil_blending',
        name: 'Oil Blending',
        description: 'Custom on-the-spot fragrance/carrier oil blending. Includes catalog, builder, customer portal, refills, and signature library.',
        productType: 'pos',
        category: 'product-specific',
        status: 'coming_soon',
        features: [
            // Feature gates (used by hasFeature() in admin & POS UI)
            'oil-blending.catalog',
            'oil-blending.blend-builder',
            'oil-blending.signature-library',
            'oil-blending.customer-history',
            'oil-blending.byob-refill',
            'oil-blending.tweak-remix',
            'oil-blending.auto-classify',
            'oil-blending.suggestion-engine',
            'oil-blending.reorder-alerts',
            'oil-blending.reports',
            'oil-blending.customer-portal',
            // POS terminal button gates (used by PosButtonLayoutTab + ActionRow)
            'pos.button.build-blend',
            'pos.button.pending-blends',
            'pos.button.customer-display',
        ],
        services: ['oil-blending-service'],
        basePrice: 0,
        displayOrder: 37,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_terminal'],
    },
    // Services package — service & rental items, drop-off job tickets,
    // appointments, check-in queue, online booking & pay, reminders, rentals.
    {
        id: 'pkg_services',
        name: 'Services',
        description: 'Service & rental items, drop-off job tickets, appointments, check-in queue, online booking & pay, reminders, rentals',
        productType: 'pos',
        category: 'product-specific',
        status: 'active',
        features: [
            'pos.services.items', 'pos.services.tickets', 'pos.services.appointments', 'pos.services.checkin',
            'pos.services.online-booking', 'pos.services.reminders', 'pos.services.rentals', 'pos.button.services',
        ],
        services: ['pos-services-service'],
        basePrice: 0,
        displayOrder: 38,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_pos_catalog', 'pkg_pos_customers'],
    },
    // NOTE: Standalone addon packages (pkg_compliance, pkg_marketing, pkg_promotions, pkg_smart_images)
    // have been consolidated into their parent POS packages:
    // - compliance-safety → pkg_pos_compliance
    // - marketing, product-reviews, promotions, deals → pkg_pos_marketing
    // - smart-image-search, ai-description, ai-insights → pkg_pos_agents
    // - loyalty-vip → pkg_pos_customers
    // RealEstate Packages (coming soon)
    {
        id: 'pkg_property',
        name: 'Property Listings',
        description: 'Property management and listings for Jesmin Real Estate',
        productType: 'realestate',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['properties', 'listings'],
        services: ['property-service'],
        basePrice: 0,
        displayOrder: 30,
        isCore: false,
        dependencies: ['pkg_core'],
    },
    {
        id: 'pkg_property_mgmt',
        name: 'Property Management',
        description: 'Lease tracking, tenant management, and maintenance for Jesmin Real Estate',
        productType: 'realestate',
        category: 'product-specific',
        status: 'coming_soon',
        features: ['leases', 'tenants-mgmt', 'maintenance'],
        services: ['property-mgmt-service'],
        basePrice: 0,
        displayOrder: 31,
        isCore: false,
        dependencies: ['pkg_core', 'pkg_property'],
    },
];
// ========================
// Utility Functions
// ========================
/**
 * Detect product type from hostname
 * Used in frontend to determine which product the user is accessing
 */
function detectProduct() {
    if (typeof window === 'undefined')
        return 'cafemaster';
    const hostname = window.location.hostname;
    // Production domains
    if (hostname.includes('cafemenumaster') || hostname.includes('coffeemasterpro')) {
        return 'cafemaster';
    }
    if (hostname.includes('agenticpos')) {
        return 'pos';
    }
    if (hostname.includes('jesminrealestate') || hostname.includes('realestate.dev')) {
        return 'realestate';
    }
    // Localhost defaults to cafemaster
    return 'cafemaster';
}
/**
 * Get product configuration by type
 */
function getProductConfig(productType) {
    const type = productType || detectProduct();
    return exports.PRODUCT_CONFIGS[type];
}
/**
 * Check if a feature is available for a product
 */
function isFeatureAvailableForProduct(feature, productType) {
    const config = exports.PRODUCT_CONFIGS[productType];
    return config.features.includes(feature);
}
exports.POS_BUTTON_REGISTRY = [
    // ── Sales ──
    { id: 'void', featureGate: 'pos.button.void', group: 'sales', label: 'Void', description: 'Void current transaction', color: '#ef4444', icon: 'X', keypadIcon: '✕' },
    { id: 'refund', featureGate: 'pos.button.refund', group: 'sales', label: 'Refund', description: 'Process refunds', color: '#f87171', icon: 'RotateCcw', keypadIcon: '↺' },
    { id: 'exchange', featureGate: 'pos.button.exchange', group: 'sales', label: 'Exchange', description: 'Exchange items', color: '#fb923c', icon: 'ArrowLeftRight', keypadIcon: '⇄' },
    { id: 'discount', featureGate: 'pos.button.discount', group: 'sales', label: 'Discount', description: 'Apply order discounts', color: '#60a5fa', icon: 'Percent', keypadIcon: '%' },
    { id: 'hold', featureGate: 'pos.button.hold', group: 'sales', label: 'Hold', description: 'Hold current order', color: '#a78bfa', icon: 'Pause', keypadIcon: '⏸' },
    { id: 'open-order', featureGate: 'pos.button.open-order', group: 'sales', label: 'Open Order', description: 'Recall held orders', color: '#c084fc', icon: 'Play', keypadIcon: '▶' },
    { id: 'search', featureGate: 'pos.button.search', group: 'sales', label: 'Search', description: 'Product search popup', color: '#3b82f6', icon: 'Search', keypadIcon: '🔍' },
    { id: 'customer', featureGate: 'pos.button.customer', group: 'sales', label: 'Customer', description: 'Customer lookup', color: '#3b82f6', icon: 'User', keypadIcon: '👤' },
    { id: 'rewards', featureGate: 'pos.button.rewards', group: 'sales', label: 'Rewards', description: 'Loyalty rewards', color: '#4ade80', icon: 'Gift', keypadIcon: '🎁' },
    { id: 'coupon', featureGate: 'pos.button.coupon', group: 'sales', label: 'Coupon', description: 'Apply coupon code', color: '#f59e0b', icon: 'Ticket', keypadIcon: '🎫' },
    { id: 'transactions', featureGate: 'pos.button.transactions', group: 'sales', label: 'Transactions', description: 'Transaction history', color: '#a5b4fc', icon: 'Receipt', keypadIcon: '📋' },
    { id: 'cash-tip', featureGate: 'pos.button.cash-tip', group: 'sales', label: 'Cash Tip', description: 'Cash tip entry', color: '#4ade80', icon: 'Coins', keypadIcon: '💰' },
    { id: 'deals', featureGate: 'pos.button.deals', group: 'sales', label: 'Deals', description: 'Deals & promotions', color: '#fbbf24', icon: 'Tag', keypadIcon: '🏷' },
    { id: 'damage', featureGate: 'pos.button.damage', group: 'sales', label: 'Damage', description: 'Mark items as damaged', color: '#f97316', icon: 'AlertTriangle', keypadIcon: '⚠' },
    { id: 'store-supply', featureGate: 'pos.button.store-supply', group: 'sales', label: 'Store Supply', description: 'Check and report store supply inventory levels', color: '#8b5cf6', icon: 'Package', keypadIcon: '📦' },
    { id: 'lottery', featureGate: 'pos.button.lottery', group: 'sales', label: 'Lotto', description: 'Lottery & gaming: sell, pay out, count', color: '#7c3aed', icon: 'Ticket', keypadIcon: '🎟' },
    // ── Register ──
    { id: 'open-drawer', featureGate: 'pos.button.open-drawer', group: 'register', label: 'Open Drawer', description: 'Open cash drawer', color: '#60a5fa', icon: 'Unlock', keypadIcon: '🔓' },
    { id: 'safe-drop', featureGate: 'pos.button.safe-drop', group: 'register', label: 'Safe Drop', description: 'Move excess cash from drawer to safe', color: '#ef4444', icon: 'Lock', keypadIcon: '🔒' },
    { id: 'calculator', featureGate: 'pos.button.calculator', group: 'register', label: 'Calculator', description: 'On-screen calculator', color: '#60a5fa', icon: 'Calculator', keypadIcon: '🔢' },
    { id: 'reports', featureGate: 'pos.button.reports', group: 'register', label: 'Reports', description: 'Shift reports', color: '#60a5fa', icon: 'BarChart3', keypadIcon: '📊' },
    // ── Employee ──
    { id: 'clock', featureGate: 'pos.button.clock', group: 'employee', label: 'Clock In/Out', description: 'Employee time clock', color: '#a78bfa', icon: 'Clock', keypadIcon: '🕒' },
    { id: 'shift-info', featureGate: 'pos.button.shift-info', group: 'employee', label: 'Shift Info', description: 'Current shift details', color: '#a78bfa', icon: 'Info', keypadIcon: 'ℹ' },
    // ── Admin ──
    { id: 'end-shift', featureGate: 'pos.button.end-shift', group: 'admin', label: 'End Shift', description: 'Close register / end shift', color: '#ef4444', icon: 'Power', keypadIcon: '⏻' },
    // ── Oil Blending (Module 29) — gated by pkg_pos_oil_blending ──
    // Tenants without the package don't see these in the layout editor at all.
    // `liveBadge: true` on pending-blends drives the WebSocket-fed count chip.
    { id: 'build-blend', featureGate: 'pos.button.build-blend', group: 'sales', label: 'Build Blend', description: 'Create a custom oil blend at the counter', color: '#10b981', icon: 'Sparkles', keypadIcon: '🌿' },
    { id: 'pending-blends', featureGate: 'pos.button.pending-blends', group: 'sales', label: 'Pending Blends', description: 'Customer-submitted blends waiting at the register', color: '#10b981', icon: 'Inbox', keypadIcon: '📥', liveBadge: true },
    { id: 'customer-display', featureGate: 'pos.button.customer-display', group: 'register', label: 'Customer Display', description: 'Open the customer-facing portal on a secondary display', color: '#3b82f6', icon: 'Monitor', keypadIcon: '🖥' },
    // ── Services (pkg_services) ──
    { id: 'services', featureGate: 'pos.button.services', group: 'sales', label: 'Services', description: 'Appointments, walk-ins, job tickets and rentals', color: '#0d9488', icon: 'CalendarClock', keypadIcon: '🗓️' },
    // ── Gas & Fuel (pkg_core) ──
    { id: 'gas', featureGate: 'pos.button.gas', group: 'sales', label: 'Gas', description: 'Sell prepaid fuel by amount or gallons', color: '#d97706', icon: 'Fuel', keypadIcon: '⛽' },
];
/**
 * Get buttons allowed by a set of feature IDs (from tenant's allowedFeatures).
 * Used by super-admin to filter which buttons can be assigned.
 */
function getButtonsForFeatures(allowedFeatures) {
    const featureSet = new Set(allowedFeatures);
    return exports.POS_BUTTON_REGISTRY.filter((btn) => featureSet.has(btn.featureGate));
}
/**
 * Get all button IDs allowed by features.
 */
function getButtonIdsForFeatures(allowedFeatures) {
    return getButtonsForFeatures(allowedFeatures).map((btn) => btn.id);
}
//# sourceMappingURL=packages.js.map